import React, { useEffect, useState, useCallback } from "react";
import { QRCodeSVG } from "qrcode.react";
import { useAuth } from "../context/AuthContext";
import { apiFetch } from "../api/client";
import { COLORS, RADIUS } from "../theme";
import Card from "../components/ui/Card";
import Btn from "../components/ui/Btn";
import Input from "../components/ui/Input";
import Badge from "../components/ui/Badge";
import Icon from "../components/ui/Icon";
import EstadoCarregando from "../components/ui/EstadoCarregando";
import EstadoVazio from "../components/ui/EstadoVazio";
import Aviso from "../components/ui/Aviso";
import MapaEscolhaLocalizacao from "../components/ui/MapaEscolhaLocalizacao";
import { buscarEndereco } from "../utils/geolocalizacao";
import { nfcDisponivel, lerNfcUmaVez } from "../utils/nfc";

const VAZIO_ROTA = { nome: "", empresa_id: "", endereco_principal: "", latitude: "", longitude: "" };
const VAZIO_PONTO = { nome: "", ordem: "", endereco: "", codigo_nfc: "" };

function labelStyle() {
  return { fontSize: 12, fontWeight: 600, color: COLORS.textMid, display: "block", marginBottom: 6 };
}

// Etiqueta pra imprimir e colar no local — abre numa aba própria só com o QR
// e o nome, já mandando pra impressão. Não guarda estado nenhum, é só uma
// janela descartável.
function imprimirEtiqueta(nomeRota, nomePonto, svgMarkup) {
  const janela = window.open("", "_blank", "width=400,height=500");
  if (!janela) return;
  janela.document.write(`
    <html>
      <head>
        <title>Etiqueta — ${nomePonto}</title>
        <style>
          body { font-family: system-ui, sans-serif; text-align: center; padding: 24px; }
          h2 { font-size: 16px; margin: 0 0 4px; }
          p { font-size: 12px; color: #666; margin: 0 0 20px; }
        </style>
      </head>
      <body>
        <h2>${nomePonto}</h2>
        <p>${nomeRota}</p>
        ${svgMarkup}
        <script>window.onload = () => window.print();</script>
      </body>
    </html>
  `);
  janela.document.close();
}

export default function Rotas() {
  const { token } = useAuth();
  const [empresas, setEmpresas] = useState([]);
  const [rotas, setRotas] = useState([]);
  const [funcionarios, setFuncionarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selecionada, setSelecionada] = useState(null);
  const [carregandoDetalhe, setCarregandoDetalhe] = useState(false);
  const [form, setForm] = useState(VAZIO_ROTA);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");

  const [formPonto, setFormPonto] = useState(VAZIO_PONTO);
  const [enderecoProprioPonto, setEnderecoProprioPonto] = useState(false);
  const [salvandoPonto, setSalvandoPonto] = useState(false);
  const [erroPonto, setErroPonto] = useState("");
  const [editandoPontoId, setEditandoPontoId] = useState(null);
  const [formEdicaoPonto, setFormEdicaoPonto] = useState(VAZIO_PONTO);
  const [lendoNfcNovo, setLendoNfcNovo] = useState(false);
  const [lendoNfcEdicao, setLendoNfcEdicao] = useState(false);

  const lerNfcParaNovo = async () => {
    setErroPonto("");
    setLendoNfcNovo(true);
    try {
      const codigo = await lerNfcUmaVez();
      setFormPonto((f) => ({ ...f, codigo_nfc: codigo }));
    } catch (e) {
      setErroPonto(e.message);
    } finally {
      setLendoNfcNovo(false);
    }
  };

  const lerNfcParaEdicao = async () => {
    setLendoNfcEdicao(true);
    try {
      const codigo = await lerNfcUmaVez();
      setFormEdicaoPonto((f) => ({ ...f, codigo_nfc: codigo }));
    } catch (e) {
      window.alert(e.message);
    } finally {
      setLendoNfcEdicao(false);
    }
  };

  const [funcionariosSelecionados, setFuncionariosSelecionados] = useState([]);
  const [buscaFuncionarioEscala, setBuscaFuncionarioEscala] = useState("");
  const [salvandoFuncionarios, setSalvandoFuncionarios] = useState(false);

  const empresasComRotas = empresas.filter((e) => e.rotas_ativo);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const [e, r, f] = await Promise.all([
        apiFetch("GET", "/api/empresas", undefined, token),
        apiFetch("GET", "/api/rotas", undefined, token),
        apiFetch("GET", "/api/funcionarios", undefined, token),
      ]);
      setEmpresas(e);
      setRotas(r);
      setFuncionarios(f);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { carregar(); }, [carregar]);

  const selecionarNova = () => {
    setSelecionada(null);
    setForm({ ...VAZIO_ROTA, empresa_id: empresasComRotas[0]?.id || "" });
    setErro("");
  };

  const carregarDetalhe = async (id) => {
    const detalhe = await apiFetch("GET", `/api/rotas/${id}`, undefined, token);
    setSelecionada(detalhe);
    setForm({
      nome: detalhe.nome,
      empresa_id: detalhe.empresa_id,
      endereco_principal: detalhe.endereco_principal || "",
      latitude: detalhe.latitude != null ? String(detalhe.latitude) : "",
      longitude: detalhe.longitude != null ? String(detalhe.longitude) : "",
    });
    setFuncionariosSelecionados(detalhe.funcionario_ids || []);
    setFormPonto(VAZIO_PONTO);
    setEnderecoProprioPonto(false);
    setEditandoPontoId(null);
  };

  const selecionar = async (rotaResumo) => {
    setErro("");
    setCarregandoDetalhe(true);
    try {
      await carregarDetalhe(rotaResumo.id);
    } finally {
      setCarregandoDetalhe(false);
    }
  };

  const salvarRota = async () => {
    setErro("");
    if (!form.nome || !form.empresa_id) {
      setErro("Informe o nome e a empresa");
      return;
    }
    setSalvando(true);
    try {
      if (selecionada) {
        await apiFetch("PUT", `/api/rotas/${selecionada.id}`, form, token);
        await carregarDetalhe(selecionada.id);
      } else {
        const criado = await apiFetch("POST", "/api/rotas", form, token);
        await carregarDetalhe(criado.id);
      }
      const lista = await apiFetch("GET", "/api/rotas", undefined, token);
      setRotas(lista);
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  };

  const alternarAtivoRota = async () => {
    await apiFetch("PUT", `/api/rotas/${selecionada.id}`, { ativo: !selecionada.ativo }, token);
    await carregarDetalhe(selecionada.id);
    setRotas(await apiFetch("GET", "/api/rotas", undefined, token));
  };

  const excluirRota = async () => {
    if (!window.confirm(`Excluir a rota "${selecionada.nome}"? Isso remove todos os pontos e vínculos dela. Essa ação não pode ser desfeita.`)) return;
    await apiFetch("DELETE", `/api/rotas/${selecionada.id}`, undefined, token);
    setSelecionada(null);
    setForm(VAZIO_ROTA);
    setRotas(await apiFetch("GET", "/api/rotas", undefined, token));
  };

  const buscarEnderecoPrincipal = async (texto) => {
    if (!texto.trim()) return;
    try {
      const resultado = await buscarEndereco(texto.trim());
      if (resultado) setForm((f) => ({ ...f, latitude: String(resultado.latitude), longitude: String(resultado.longitude) }));
    } catch {
      // busca de apoio, silenciosa — quem quiser precisão ajusta no mapa
    }
  };

  const adicionarPonto = async () => {
    setErroPonto("");
    if (!formPonto.nome.trim()) {
      setErroPonto("Informe o nome do ponto");
      return;
    }
    setSalvandoPonto(true);
    try {
      await apiFetch("POST", `/api/rotas/${selecionada.id}/pontos`, {
        nome: formPonto.nome,
        ordem: formPonto.ordem || (selecionada.pontos?.length || 0) + 1,
        endereco: enderecoProprioPonto ? formPonto.endereco : "",
        codigo_nfc: formPonto.codigo_nfc,
      }, token);
      setFormPonto(VAZIO_PONTO);
      setEnderecoProprioPonto(false);
      await carregarDetalhe(selecionada.id);
    } catch (e) {
      setErroPonto(e.message);
    } finally {
      setSalvandoPonto(false);
    }
  };

  const iniciarEdicaoPonto = (ponto) => {
    setEditandoPontoId(ponto.id);
    setFormEdicaoPonto({ nome: ponto.nome, ordem: ponto.ordem, endereco: ponto.endereco || "", codigo_nfc: ponto.codigo_nfc || "" });
  };

  const salvarEdicaoPonto = async (pontoId) => {
    try {
      await apiFetch("PUT", `/api/rotas/${selecionada.id}/pontos/${pontoId}`, formEdicaoPonto, token);
      setEditandoPontoId(null);
      await carregarDetalhe(selecionada.id);
    } catch (e) {
      window.alert(e.message);
    }
  };

  const alternarAtivoPonto = async (ponto) => {
    await apiFetch("PUT", `/api/rotas/${selecionada.id}/pontos/${ponto.id}`, { ativo: !ponto.ativo }, token);
    await carregarDetalhe(selecionada.id);
  };

  const excluirPonto = async (ponto) => {
    if (!window.confirm(`Excluir o ponto "${ponto.nome}"?`)) return;
    await apiFetch("DELETE", `/api/rotas/${selecionada.id}/pontos/${ponto.id}`, undefined, token);
    await carregarDetalhe(selecionada.id);
  };

  const alternarFuncionario = (id) => {
    setFuncionariosSelecionados((atual) =>
      atual.includes(id) ? atual.filter((x) => x !== id) : [...atual, id]
    );
  };

  const salvarFuncionarios = async () => {
    setSalvandoFuncionarios(true);
    try {
      await apiFetch("PUT", `/api/rotas/${selecionada.id}/funcionarios`, { funcionario_ids: funcionariosSelecionados }, token);
      await carregarDetalhe(selecionada.id);
    } finally {
      setSalvandoFuncionarios(false);
    }
  };

  if (loading) return <EstadoCarregando />;

  if (empresasComRotas.length === 0) {
    return (
      <div>
        <div style={{ marginBottom: 24 }}>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: COLORS.text, margin: "0 0 4px", letterSpacing: "-0.02em" }}>
            Rotas de Serviço
          </h1>
          <p style={{ fontSize: 13.5, color: COLORS.textMid, margin: 0 }}>
            Cronogramas de limpeza, ronda e vistoria com verificação por NFC/QR code.
          </p>
        </div>
        <Card>
          <EstadoVazio
            icon="route"
            texto="Nenhuma das suas empresas tem o módulo de Rotas habilitado ainda. Fale com o suporte PontoZipar pra ativar."
          />
        </Card>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 800, color: COLORS.text, margin: "0 0 4px", letterSpacing: "-0.02em" }}>
          Rotas de Serviço
        </h1>
        <p style={{ fontSize: 13.5, color: COLORS.textMid, margin: 0 }}>
          Cronogramas de limpeza, ronda e vistoria com verificação por NFC/QR code.
        </p>
      </div>

      <div className="split-layout">
        <Card style={{ padding: 0 }} className="split-layout-sidebar">
          <div style={{ padding: "14px 16px", borderBottom: `1px solid ${COLORS.border}` }}>
            <Btn onClick={selecionarNova} style={{ width: "100%" }}>+ Nova rota</Btn>
          </div>
          <div className="mobile-scroll-relax" style={{ maxHeight: 560, overflowY: "auto" }}>
            {rotas.length === 0 ? (
              <EstadoVazio icon="route" texto="Nenhuma rota cadastrada ainda." />
            ) : (
              rotas.map((r) => (
                <button
                  key={r.id}
                  onClick={() => selecionar(r)}
                  style={{
                    display: "block", width: "100%", textAlign: "left", padding: "12px 16px",
                    background: selecionada?.id === r.id ? COLORS.primaryBg : "transparent",
                    border: "none", borderBottom: `1px solid ${COLORS.border}`, cursor: "pointer",
                  }}
                >
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: COLORS.text }}>{r.nome}</div>
                  <div style={{ fontSize: 11.5, color: COLORS.textLight, marginTop: 2 }}>
                    {r.empresa_nome} · {r.total_pontos} {r.total_pontos === 1 ? "ponto" : "pontos"} {!r.ativo && "· Inativa"}
                  </div>
                </button>
              ))
            )}
          </div>
        </Card>

        <Card style={{ flex: 1 }}>
          {carregandoDetalhe ? (
            <EstadoCarregando />
          ) : (
            <>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
                <h2 style={{ fontSize: 16, fontWeight: 800, color: COLORS.text, margin: 0 }}>
                  {selecionada ? selecionada.nome : "Nova rota"}
                </h2>
                {selecionada && <Badge tone={selecionada.ativo ? "success" : "neutral"}>{selecionada.ativo ? "Ativa" : "Inativa"}</Badge>}
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <Input label="Nome da rota" value={form.nome} onChange={(e) => { setForm({ ...form, nome: e.target.value }); setErro(""); }} placeholder="Ex: Limpeza — Edifício Central" />

                <div>
                  <label style={labelStyle()}>Empresa</label>
                  <select
                    value={form.empresa_id}
                    onChange={(e) => { setForm({ ...form, empresa_id: e.target.value }); setErro(""); }}
                    style={{ width: "100%", border: `1.5px solid ${COLORS.border}`, borderRadius: RADIUS.md, padding: "11px 14px", fontSize: 14, background: COLORS.bg, color: COLORS.text }}
                  >
                    {empresasComRotas.map((e) => <option key={e.id} value={e.id}>{e.nome}</option>)}
                  </select>
                </div>

                <Input
                  label="Endereço principal"
                  value={form.endereco_principal}
                  onChange={(e) => setForm({ ...form, endereco_principal: e.target.value })}
                  onBlur={(e) => buscarEnderecoPrincipal(e.target.value)}
                  placeholder="Rua, número, bairro, cidade"
                />
                <MapaEscolhaLocalizacao
                  latitude={form.latitude ? Number(form.latitude) : null}
                  longitude={form.longitude ? Number(form.longitude) : null}
                  onChange={(lat, lon) => setForm((f) => ({ ...f, latitude: String(lat), longitude: String(lon) }))}
                  altura={200}
                />

                {erro && (
                  <div style={{ color: COLORS.danger, fontSize: 13, background: COLORS.dangerBg, border: "1px solid #FECACA", borderRadius: 8, padding: "9px 13px" }}>
                    {erro}
                  </div>
                )}

                <div style={{ display: "flex", gap: 10 }}>
                  <Btn onClick={salvarRota} disabled={salvando} style={{ flex: 1 }}>
                    {salvando ? "Salvando..." : selecionada ? "Salvar alterações" : "Criar rota"}
                  </Btn>
                  {selecionada && (
                    <>
                      <Btn variant="secondary" onClick={alternarAtivoRota}>{selecionada.ativo ? "Desativar" : "Ativar"}</Btn>
                      <Btn variant="secondary" onClick={excluirRota} style={{ color: COLORS.danger }}>Excluir</Btn>
                    </>
                  )}
                </div>
              </div>

              {selecionada && (
                <>
                  {/* ── Pontos de verificação ── */}
                  <div style={{ borderTop: `1px solid ${COLORS.border}`, marginTop: 24, paddingTop: 20 }}>
                    <p style={{ fontSize: 11.5, fontWeight: 700, color: COLORS.primary, textTransform: "uppercase", letterSpacing: "0.06em", margin: "0 0 12px" }}>
                      Pontos de verificação ({selecionada.pontos.length})
                    </p>

                    {selecionada.pontos.length > 0 && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
                        {selecionada.pontos.map((p) => (
                          <div key={p.id} style={{ border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.md, padding: "10px 14px" }}>
                            {editandoPontoId === p.id ? (
                              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                                <div style={{ display: "flex", gap: 8 }}>
                                  <div style={{ flex: 2 }}>
                                    <Input placeholder="Nome" value={formEdicaoPonto.nome} onChange={(e) => setFormEdicaoPonto({ ...formEdicaoPonto, nome: e.target.value })} />
                                  </div>
                                  <div style={{ flex: 1 }}>
                                    <Input placeholder="Ordem" type="number" value={formEdicaoPonto.ordem} onChange={(e) => setFormEdicaoPonto({ ...formEdicaoPonto, ordem: e.target.value })} />
                                  </div>
                                </div>
                                <Input placeholder="Endereço (opcional, se diferente do principal)" value={formEdicaoPonto.endereco} onChange={(e) => setFormEdicaoPonto({ ...formEdicaoPonto, endereco: e.target.value })} />
                                <div style={{ display: "flex", gap: 8 }}>
                                  <div style={{ flex: 1 }}>
                                    <Input placeholder="Código NFC (opcional)" value={formEdicaoPonto.codigo_nfc} onChange={(e) => setFormEdicaoPonto({ ...formEdicaoPonto, codigo_nfc: e.target.value })} />
                                  </div>
                                  {nfcDisponivel() && (
                                    <Btn variant="secondary" onClick={lerNfcParaEdicao} disabled={lendoNfcEdicao}>
                                      {lendoNfcEdicao ? "Encoste o cartão..." : "Ler NFC"}
                                    </Btn>
                                  )}
                                </div>
                                <div style={{ display: "flex", gap: 8 }}>
                                  <Btn onClick={() => salvarEdicaoPonto(p.id)} style={{ flex: 1 }}>Salvar</Btn>
                                  <Btn variant="secondary" onClick={() => setEditandoPontoId(null)}>Cancelar</Btn>
                                </div>
                              </div>
                            ) : (
                              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                                <div style={{ width: 44, height: 44, flexShrink: 0, padding: 4, background: "#fff", border: `1px solid ${COLORS.border}`, borderRadius: 6 }}>
                                  <QRCodeSVG value={p.codigo_qr} size={36} />
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: 13.5, fontWeight: 700, color: COLORS.text }}>
                                    #{p.ordem} · {p.nome} {!p.ativo && <span style={{ color: COLORS.textLight, fontWeight: 500 }}>(inativo)</span>}
                                  </div>
                                  <div style={{ fontSize: 11.5, color: COLORS.textLight, marginTop: 2, display: "flex", alignItems: "center", gap: 10 }}>
                                    {p.endereco && <span>{p.endereco}</span>}
                                    <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                      <Icon name="nfc" size={12} />
                                      {p.codigo_nfc ? "NFC pareado" : "sem NFC"}
                                    </span>
                                  </div>
                                </div>
                                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                                  <button
                                    onClick={() => {
                                      const svg = document.querySelector(`#qr-imprimir-${p.id}`);
                                      imprimirEtiqueta(selecionada.nome, p.nome, svg ? svg.outerHTML : "");
                                    }}
                                    style={botaoMini()}
                                  >
                                    Imprimir
                                  </button>
                                  <button onClick={() => iniciarEdicaoPonto(p)} style={botaoMini()}>Editar</button>
                                  <button onClick={() => alternarAtivoPonto(p)} style={botaoMini()}>{p.ativo ? "Desativar" : "Ativar"}</button>
                                  <button onClick={() => excluirPonto(p)} style={{ ...botaoMini(), color: COLORS.danger }}>Excluir</button>
                                </div>
                                <div style={{ display: "none" }}>
                                  <QRCodeSVG id={`qr-imprimir-${p.id}`} value={p.codigo_qr} size={200} />
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    <div style={{ background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.md, padding: 14 }}>
                      <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                        <div style={{ flex: 2 }}>
                          <Input placeholder="Nome do ponto (ex: Sala 3, Portaria)" value={formPonto.nome} onChange={(e) => setFormPonto({ ...formPonto, nome: e.target.value })} />
                        </div>
                        <div style={{ width: 90 }}>
                          <Input placeholder="Ordem" type="number" value={formPonto.ordem} onChange={(e) => setFormPonto({ ...formPonto, ordem: e.target.value })} />
                        </div>
                      </div>
                      <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, color: COLORS.textMid, marginBottom: 8, cursor: "pointer" }}>
                        <input type="checkbox" checked={enderecoProprioPonto} onChange={(e) => setEnderecoProprioPonto(e.target.checked)} style={{ width: 15, height: 15 }} />
                        Esse ponto fica num endereço diferente do principal
                      </label>
                      {enderecoProprioPonto && (
                        <div style={{ marginBottom: 8 }}>
                          <Input placeholder="Endereço desse ponto" value={formPonto.endereco} onChange={(e) => setFormPonto({ ...formPonto, endereco: e.target.value })} />
                        </div>
                      )}
                      <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                        <div style={{ flex: 1 }}>
                          <Input placeholder="Código do cartão NFC (opcional — dá pra parear depois)" value={formPonto.codigo_nfc} onChange={(e) => setFormPonto({ ...formPonto, codigo_nfc: e.target.value })} />
                        </div>
                        {nfcDisponivel() && (
                          <Btn variant="secondary" onClick={lerNfcParaNovo} disabled={lendoNfcNovo}>
                            {lendoNfcNovo ? "Encoste o cartão..." : "Ler NFC"}
                          </Btn>
                        )}
                      </div>
                      {!nfcDisponivel() && (
                        <p style={{ fontSize: 11, color: COLORS.textLight, margin: "-4px 0 10px" }}>
                          Leitura de NFC só funciona no Chrome do Android. Pra parear um cartão, abra essa tela pelo celular — ou deixe em branco e use só o QR code.
                        </p>
                      )}
                      {erroPonto && (
                        <div style={{ color: COLORS.danger, fontSize: 13, background: COLORS.dangerBg, border: "1px solid #FECACA", borderRadius: 8, padding: "9px 13px", marginBottom: 10 }}>
                          {erroPonto}
                        </div>
                      )}
                      <Btn onClick={adicionarPonto} disabled={salvandoPonto} style={{ width: "100%" }}>
                        {salvandoPonto ? "Adicionando..." : "+ Adicionar ponto"}
                      </Btn>
                    </div>

                    <Aviso tone="info" icon="qrCode">
                      Todo ponto ganha um QR code automático — imprima e cole no local. O código NFC é opcional e pode ser adicionado a qualquer momento, editando o ponto.
                    </Aviso>
                  </div>

                  {/* ── Funcionários escalados ── */}
                  <div style={{ borderTop: `1px solid ${COLORS.border}`, marginTop: 24, paddingTop: 20 }}>
                    <p style={{ fontSize: 11.5, fontWeight: 700, color: COLORS.primary, textTransform: "uppercase", letterSpacing: "0.06em", margin: "0 0 12px" }}>
                      Funcionários escalados nessa rota
                    </p>
                    {funcionarios.length === 0 ? (
                      <p style={{ fontSize: 13, color: COLORS.textMid }}>Nenhum funcionário cadastrado ainda.</p>
                    ) : (
                      <>
                      <Input
                        placeholder="Buscar funcionário..."
                        value={buscaFuncionarioEscala}
                        onChange={(e) => setBuscaFuncionarioEscala(e.target.value)}
                        style={{ marginBottom: 8 }}
                      />
                      <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 220, overflowY: "auto", marginBottom: 12 }}>
                        {funcionarios.filter((f) => f.nome.toLowerCase().includes(buscaFuncionarioEscala.toLowerCase())).map((f) => (
                          <label key={f.id} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13.5, color: COLORS.text, padding: "6px 4px", cursor: "pointer" }}>
                            <input
                              type="checkbox"
                              checked={funcionariosSelecionados.includes(f.id)}
                              onChange={() => alternarFuncionario(f.id)}
                              style={{ width: 16, height: 16 }}
                            />
                            {f.nome} {f.cargo && <span style={{ color: COLORS.textLight }}>— {f.cargo}</span>}
                          </label>
                        ))}
                      </div>
                      </>
                    )}
                    <Btn onClick={salvarFuncionarios} disabled={salvandoFuncionarios} style={{ width: "100%" }}>
                      {salvandoFuncionarios ? "Salvando..." : "Salvar escalação"}
                    </Btn>
                  </div>
                </>
              )}
            </>
          )}
        </Card>
      </div>
    </div>
  );
}

function botaoMini() {
  return {
    background: "transparent", border: `1.5px solid ${COLORS.border}`, borderRadius: 8,
    padding: "5px 9px", fontSize: 11.5, fontWeight: 600, color: COLORS.textMid, cursor: "pointer",
  };
}
